// The Commons, for visitors: GET /commons loads this. The main island, where
// humans arrive: the plaza round its fountain, the Dance Hall on its north
// side with a marquee saying what's on, prime plots ringing the plaza (for
// sale, reserved by the crown, or someone's), a path west to the homestead,
// and a bridge south to the playa, the festival island of free-for-all lots
// round a wooden effigy, with theme camps and whatever the plazmi have built.
// The plazmi who are on the plaza or the playa wander there. The page polls
// /api/commons.
// Past it, over a plank bridge to the east, lie the wilds (wilds.ts): the
// woodlot, the quarry, the meadow and the scrapyard, where plazmi gather
// materials; and the market's stalls line the north side of the plaza.
// The Commons windmill stands in the north-east corner: residents take a turn
// at its sails (wind), which turn with the day's wind. The light follows world
// time, as on a house page (daylight.ts): dawn to night, lamps and lanterns lit
// after dark, stars over the playa.
//   ?at=playa  start on the playa     ?at=wilds  start in the wilds     ?still  one frame (screenshots)
//   ?time=21   hold the light at that hour
import './perf/singlepass.js'; // (P84) first: before any material is made
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { setStyle, finishShadows, mat, cyl, sphere, rbox, lathe, at } from './kit.js';
import { makeRenderer, makeStage, makeCamera, island, clouds } from './stage.js';
import { makeComposer } from './post.js';
import { fitView } from './fit.js';
import { govern, startPR, watchFrom } from './quality.js';
import { early, stage as loadStage } from './loading.js';
import { arrival, passage } from './exit.js';
import { followPage, plazmiCard, startFollow } from './follow.js';
import { outAndAboutTab } from './nowlist.js';
import { wbox, buildHouse } from './house.js';
import { surf } from './surfaces.js';
import * as Y from './yard.js';
import { fountain, noticeBoard } from './scenes.js';
import { flagpole } from './parts.js';
import { PLAZMI_FLAG } from './pixel.js';
import { Fx } from './fx.js';
import { visitor as toyFigure } from './chars.js';
import type { IdleGround, Seat } from './crowd.js';
import { Troupe, floorHouse, injectStyles, letters, mergeStatic, BEAT_MS, type P, type PlaceLive } from './place.js';
import { rng } from './util.js';
import { homeSign, guide } from './door.js';
import { shell } from './ui/shell.js';
import { Talk, isMe } from './ui/talk.js';
import { walkerNow } from './chatline.js';
import { moveTags } from './movetags.js';
import { waysToGo, onTab } from './ui/places.js';
import { playaPiece, type Piece as PlayaPiece } from './pieces.js';
import * as Fu from './furnish.js';
import { STALLS, stallModel, emptyStall, type Stall } from './wilds.js';
import { cartModel, CART_AT, CART_FOOT, type Cart } from './cart.js';
// the playa as a ring city and the wilds as gathering grounds, laid out as data (lands.ts, playacity.ts, wildsland.ts)
import { loadLands, type Ground } from './lands.js';
import { PlayaCity, campBanner } from './playacity.js';
import { WildsLand, workSpots } from './wildsland.js';
import { commonsWindmill } from './machines.js';
import { Daylight, fixedHourFrom } from './daylight.js';
import { soundPage } from './sound/index.js';
// the town round the plaza and the playa's big things (town.ts, playa-builds.ts)
import { Town, TOWN, townAt, townWalkGrounds, PLAZA_THINGS, primeExtras, wildflowers, splitMaterials, plotSignLines, plotHover, type TownLive } from './town.js';
import { roomPath, ROOMS, DOORS, type RoomId } from './townpages.js';
import { CAMPUS_GROUNDS, CAMPUS_LAMPS } from './campus.js';
import { playaBuild, effigyGrown, buildText, raisedBy, burningThing, type Build, type Ash } from './playa-builds.js';
import { placeWalk, walkQuery, BODY_R } from './walk.js';
import { Jobs, type Step } from './placework.js';
import type { Life } from './life.js';
import './actions/places.js';
import { camControls, clickGuard } from './camctl.js';
import { sinceCommons } from './since.js';
// the prime district round the island: rings of quarters, lanes, squares and plots (district.ts)
import { District, loadDistrict, type PlotsLive } from './district.js';
// (P83) the view distance: fog, the far plane, cells and simpler far parts (viewdist.ts)
import { applyView, chunkInstanced, FarDetail } from './viewdist.js';

const q = new URLSearchParams(location.search);
const still = q.has('still');
const atPlaya = q.get('at') === 'playa';
const atWilds = q.get('at') === 'wilds';
// /@name/prime comes here: the Commons, framed on that prime plot
const focusPlot = +(q.get('plot') || 0);
const ui = document.getElementById('ui') as HTMLElement;
const POLL_MS = 4000;
const stats = { fps: 0, pr: 0, calls: 0, tris: 0 };
(window as any).__plazmi = stats;
function done() { (window as any).__ready = true; }
const el = (tag: string, cls = '', text = '') => { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; };

// ---------------------------------------------------------------------------
// where things are (world tiles)

const PLAZA = { x0: 26, z0: 22, x1: 54, z1: 42 };
const FOUNTAIN: P = [40, 32];
const HALL = { x0: 32, z0: 6, x1: 48, z1: 20 };
const PLOTS: Record<number, P> = { 7: [14, 4], 8: [52, 4], 1: [4, 19], 2: [4, 33], 3: [62, 19], 4: [62, 33], 5: [18, 44.5], 6: [48, 44.5] };
// the playa's crowd floor round the effigy, and the effigy: set from the ring city's layout (playacity.ts)
let PLAYA = { x0: 16, z0: 61, x1: 64, z1: 92 };
let EFFIGY: P = [40, 72.5];
const MILL: P = [22.4, 18.6];
// the flowerbeds (centre x, z; width, depth; colours; seed): beside the paths in, so the way
// from the south edge to the fountain, and from the fountain to the hall's doors, is clear
// (the south pair stays behind the row where the plaza's audience stands, z 40.5)
const BEDS: [number, number, number, number, string[], number][] = [
  [34.4, 41.45, 4, 0.6, ['#e25d6a', '#f2c14e', '#f6f0e6', '#7aa7d9'], 3], [45.6, 41.45, 4, 0.6, ['#e25d6a', '#f2c14e', '#f6f0e6', '#7aa7d9'], 4],
  [34.0, 21.0, 3.2, 0.7, ['#e48aa0', '#f6e7ee', '#e9b44c'], 5], [46.0, 21.0, 3.2, 0.7, ['#e48aa0', '#f6e7ee', '#e9b44c'], 6],
];

type Piece = PlayaPiece & { at: P; size: P; by: string; by_name: string; admirers: number };
type Lot = { n: number; size: P; camp: { name: string; vibe?: string; crew: string; crew_name?: string } | null; pieces: Piece[]; builds?: Build[] };
type Playa = { lots: Lot[]; effigy: { planks: number; latest: { who: string; text: string }[] }; theme?: { name: string; hint: string };
  burned: { at: number; planks: number; pieces: number; theme?: string; next_theme?: string; ashes?: Ash[] } | null };
type Plot = { n: number; size: P; side: string; status: 'for-sale' | 'owned' | 'reserved'; owner?: string; owner_name?: string;
  // what the owner built: a garden of things and a pavilion in its home's colours
  yard?: Fu.Thing[]; pavilion?: any; designs?: Fu.Design[];
  // its own words (name plot, sign plot) and its building's kind (P88)
  name?: string; sign?: string; kind?: string; kind_name?: string; private?: boolean; page?: string };
type Wild = PlaceLive & { gives: string[]; gathering: { slug: string; name: string; material: string; ends: number }[]; trips_today: number };
type Market = { stalls: Stall[]; lately: string | null; limited: { key: string; name: string; glow: number } | null };
type Commons = {
  now: number; plaza: PlaceLive; playa: PlaceLive & { playa: Playa };
  wilds: Record<string, Wild>; market: Market;
  // the pedlar's cart, on Wednesdays (cart.ts)
  cart?: Cart;
  windmill?: { wind: string; weather: string; turns: { slug: string; name: string; at: number }[] };
  hall: { inside: number; marquee: { title: string; line: string }; showing: { line: string; title: string; at: number } | null };
  plots: { price: number; released: number; plots: Plot[] } & PlotsLive; homes: number;
  town?: TownLive;
};

// ---------------------------------------------------------------------------
// the Dance Hall, from outside

function hallBuilding(board: THREE.Mesh, bulbs: THREE.InstancedMesh[], windows: THREE.Material[]) {
  const g = new THREE.Group();
  const { x0, z0, x1, z1 } = HALL, cx = (x0 + x1) / 2;
  const cream = surf({ kind: 'stucco', color: '#f3e3c8' }), trim = mat('#c8547e', { rough: 0.6 }), teal = mat('#3f8f8a', { rough: 0.5 });
  const gold = mat('#d6b05a', { rough: 0.35, metal: 0.6 });
  g.add(wbox(x0, 0, z0, x1, 6.2, z1 - 0.3, cream));
  g.add(wbox(x0 - 0.05, 0, z0 - 0.05, x1 + 0.05, 0.7, z1 - 0.25, mat('#b9927a', { rough: 0.9 })));
  g.add(wbox(x0 - 0.08, 6.0, z0 - 0.08, x1 + 0.08, 6.3, z1 - 0.2, trim));
  // a barrel roof in teal standing seam
  const roof = new THREE.Mesh(new THREE.CylinderGeometry(8.4, 8.4, z1 - z0 + 0.2, 36, 1, false, -Math.PI / 2, Math.PI), surf({ kind: 'seam', color: '#4f9a92' }));
  roof.rotation.x = -Math.PI / 2; roof.scale.set(1, 1, 0.42); roof.position.set(cx, 6.25, (z0 + z1) / 2 - 0.2); g.add(roof);
  // the stepped front, with a round sign on top
  g.add(wbox(x0 - 0.3, 0, z1 - 0.4, x1 + 0.3, 7.0, z1 + 0.1, cream));
  g.add(wbox(cx - 5.5, 7.0, z1 - 0.4, cx + 5.5, 8.6, z1 + 0.1, cream));
  g.add(wbox(cx - 3, 8.6, z1 - 0.4, cx + 3, 9.9, z1 + 0.1, cream));
  for (const [a, b, y] of [[x0 - 0.35, x1 + 0.35, 7.0], [cx - 5.55, cx + 5.55, 8.6], [cx - 3.05, cx + 3.05, 9.9]]) g.add(wbox(a, y, z1 - 0.45, b, y + 0.22, z1 + 0.18, trim));
  const disc = new THREE.Mesh(new THREE.CylinderGeometry(1.55, 1.55, 0.3, 40), mat('#1c1620', { rough: 0.5 }));
  disc.rotation.x = Math.PI / 2; disc.position.set(cx, 11.0, z1 - 0.15); g.add(disc);
  const ring = new THREE.Mesh(new THREE.TorusGeometry(1.55, 0.12, 10, 40), gold); ring.position.set(cx, 11.0, z1 - 0.05); g.add(ring);
  const emblem = new THREE.Mesh(new THREE.PlaneGeometry(2.6, 2.6), new THREE.MeshBasicMaterial({ map: letters([{ text: '♪', size: 150, color: '#ffcf5c', font: 'serif', weight: 400 }], 256, 256, null), transparent: true, toneMapped: false }));
  emblem.position.set(cx, 11.0, z1 + 0.02); g.add(emblem);
  // DANCE HALL in big letters across the front
  const name = new THREE.Mesh(new THREE.PlaneGeometry(9.6, 1.25), new THREE.MeshStandardMaterial({ map: letters([{ text: 'DANCE HALL', size: 100, color: '#c8547e' }], 1024, 132, null), transparent: true, roughness: 0.6 }));
  name.position.set(cx, 7.85, z1 + 0.12); g.add(name);
  // round windows down the west side, glowing
  for (const z of [9, 12.5, 16]) {
    const w = new THREE.Mesh(new THREE.CircleGeometry(0.85, 24), windows[0]);
    w.rotation.y = -Math.PI / 2; w.position.set(x0 - 0.02, 3.6, z); g.add(w);
    const fr = new THREE.Mesh(new THREE.TorusGeometry(0.85, 0.1, 8, 24), gold); fr.rotation.y = -Math.PI / 2; fr.position.set(x0 - 0.04, 3.6, z); g.add(fr);
  }
  for (const x of [x0 + 2.2, x1 - 2.2]) {
    const w = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 2.4), windows[1]);
    w.position.set(x, 3.0, z1 + 0.12); g.add(w);
    const arch = new THREE.Mesh(new THREE.CircleGeometry(0.8, 20, 0, Math.PI), windows[1]); arch.position.set(x, 4.2, z1 + 0.12); g.add(arch);
    g.add(wbox(x - 0.9, 1.7, z1 + 0.1, x + 0.9, 1.8, z1 + 0.3, trim));
  }
  // doors, and a marquee over them
  g.add(wbox(cx - 1.5, 0, z1 + 0.08, cx + 1.5, 3.1, z1 + 0.16, teal));
  g.add(wbox(cx - 0.03, 0, z1 + 0.16, cx + 0.03, 3.1, z1 + 0.2, gold));
  for (const s of [-1, 1]) { const p = new THREE.Mesh(new THREE.CircleGeometry(0.32, 18), windows[1]); p.position.set(cx + s * 0.75, 2.1, z1 + 0.18); g.add(p); }
  const canopy = rbox(9, 1.15, 2.2, 0.08, mat('#1c1620', { rough: 0.5 }));
  canopy.position.set(cx, 3.95, z1 + 1.1); g.add(canopy);
  g.add(wbox(cx - 4.6, 3.32, z1 + 0.05, cx + 4.6, 3.4, z1 + 2.3, gold));
  board.userData.keep = true;
  board.position.set(cx, 3.96, z1 + 2.23); g.add(board);
  for (const s of [-1, 1]) g.add(at(cyl(0.06, 0.06, 3.4, gold, 8), cx + s * 4.3, 1.7, z1 + 2.0));
  // bulbs round the canopy, chasing
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i <= 26; i++) pts.push(new THREE.Vector3(cx - 4.5 + (9 * i) / 26, 4.56, z1 + 2.25), new THREE.Vector3(cx - 4.5 + (9 * i) / 26, 3.36, z1 + 2.25));
  for (let i = 1; i < 6; i++) pts.push(new THREE.Vector3(cx - 4.52, 4.56, z1 + 0.05 + (2.2 * i) / 6), new THREE.Vector3(cx + 4.52, 4.56, z1 + 0.05 + (2.2 * i) / 6));
  const m4 = new THREE.Matrix4();
  pts.forEach((p, i) => bulbs[i % 2].setMatrixAt(Math.floor(i / 2), m4.makeTranslation(p.x, p.y, p.z)));
  bulbs.forEach((b) => { b.count = Math.floor(pts.length / 2); g.add(b); });
  // a red carpet, and topiaries
  g.add(wbox(cx - 1.1, 0.01, z1 + 0.1, cx + 1.1, 0.04, PLAZA.z0, mat('#b8456e', { rough: 1 })));
  for (const s of [-1, 1]) {
    g.add(at(lathe([[0, 0], [0.4, 0], [0.46, 0.55], [0.5, 0.6], [0, 0.6]], mat('#d6b05a', { rough: 0.5 }), 18), cx + s * 2.4, 0, z1 + 1.0));
    const ball = Y.foliage(0.55, 0.6, 0.55, '#4f7a42', 3 + s); ball.position.set(cx + s * 2.4, 1.3, z1 + 1.0); g.add(ball);
    const top = Y.foliage(0.35, 0.38, 0.35, '#5a8a48', 5 + s); top.position.set(cx + s * 2.4, 2.1, z1 + 1.0); g.add(top);
  }
  // bunting from the hall to the plaza's lamp posts
  for (const [a, b] of [[[x0 + 0.2, 6.1, z1 + 0.2], [PLAZA.x0 + 2, 3.2, PLAZA.z0 + 1.5]], [[x1 - 0.2, 6.1, z1 + 0.2], [PLAZA.x1 - 2, 3.2, PLAZA.z0 + 1.5]]] as number[][][]) {
    const n = 16;
    for (let i = 1; i < n; i++) {
      const t = i / n, p = new THREE.Vector3(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t - Math.sin(Math.PI * t) * 0.8, a[2] + (b[2] - a[2]) * t);
      const f = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.42, 3), mat(['#e86a92', '#f2c14e', '#5fb3d9', '#8fcf6a', '#b98ae8'][i % 5], { rough: 0.8 }));
      f.rotation.x = Math.PI; f.position.copy(p).y -= 0.2; f.userData.noShadow = true; g.add(f);
    }
  }
  return g;
}

// ---------------------------------------------------------------------------
// prime plots: empty lots, staked out, with a sign

function sign(lines: { text: string; size: number; color: string }[], bg: string, w = 2.2, h = 1.1) {
  const g = new THREE.Group(), wood = mat('#6e4b33', { rough: 0.8 });
  g.add(at(cyl(0.06, 0.07, 1.9, wood, 8), -w / 2 + 0.15, 0.95, -0.12), at(cyl(0.06, 0.07, 1.9, wood, 8), w / 2 - 0.15, 0.95, -0.12)); // stakes behind the board
  g.add(wbox(-w / 2 - 0.05, 0.75, -0.05, w / 2 + 0.05, 0.85 + h, 0.05, wood));
  const face = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat('#ffffff', { map: letters(lines, Math.round(256 * w), Math.round(256 * h), bg), rough: 0.9, tint: '#e4ddd0' }));
  face.position.set(0, 0.8 + h / 2, 0.056); g.add(face);
  return g;
}

function plotLot(p: Plot, price: number) {
  const g = new THREE.Group();
  const [w, d] = p.size, owned = p.status === 'owned';
  g.add(wbox(0.2, 0.004, 0.2, w - 0.2, 0.012, d - 0.2, surf({ kind: 'grass', color: owned ? '#93b462' : '#8fae66', stripes: true })));
  const stake = mat('#c9a06a', { rough: 0.8 }), tip = mat(p.status === 'reserved' ? '#7d6fd1' : '#e8803a', { rough: 0.6 });
  const corners: P[] = [[0.3, 0.3], [w - 0.3, 0.3], [w - 0.3, d - 0.3], [0.3, d - 0.3]];
  for (const [x, z] of corners) { g.add(at(cyl(0.05, 0.05, 0.7, stake, 6), x, 0.35, z)); g.add(at(cyl(0.055, 0.055, 0.12, tip, 6), x, 0.72, z)); }
  const line = new THREE.LineLoop(new THREE.BufferGeometry().setFromPoints(corners.map(([x, z]) => new THREE.Vector3(x, 0.6, z))), new THREE.LineBasicMaterial({ color: p.status === 'reserved' ? '#7d6fd1' : '#e8803a' }));
  g.add(line);
  const lines = p.status === 'owned'
    ? plotSignLines(p)
    : p.status === 'for-sale'
      ? [{ text: 'FOR SALE', size: 56, color: '#c8547e' }, { text: `plot ${p.n} · ${price} coins`, size: 36, color: '#3a2a20' }]
      : [{ text: 'RESERVED', size: 52, color: '#5a4fa8' }, { text: `by the crown · plot ${p.n}`, size: 32, color: '#3a2a20' }];
  const s = sign(lines, owned ? '#eef5e6' : '#fbf5e8');
  // (P88) to one side of the front, so the way to the building's door is clear
  s.position.set(owned ? Math.min(w / 2, 1.7) : w / 2, 0, d - 0.6); g.add(s);
  if (owned) { const f = flagpole(PLAZMI_FLAG, { h: 3.2 }); f.position.set(w - 1, 0, d - 1.2); g.add(f); }
  // what the owner built there: a pavilion in the colours of its home, and the garden round it
  if (owned && p.pavilion) { g.add(buildHouse(p.pavilion, {})); g.add(primeExtras(p.pavilion.kind, p.pavilion, p.owner_name || '')); }
  if (!owned) g.add(wildflowers(w, d, p.n));
  if (owned && p.yard?.length) {
    const h = { version: 0, plot: p.size, spec: p.pavilion || { masses: [] }, furniture: [], yard: p.yard, designs: p.designs || [], chores: {}, resident: { name: p.owner_name || '', color: '#fff', glow: '#fff' } } as Fu.House;
    g.add(Fu.yard(h));
  }
  return g;
}

// ---------------------------------------------------------------------------
// the playa: pieces, camps and the effigy


// the wooden effigy: playa-builds.ts (effigyGrown)

// festival night: fireflies rising round the effigy, wobbling, fading, rising again
function fireflies(n = 170) {
  const c = document.createElement('canvas'); c.width = c.height = 32;
  const g2 = c.getContext('2d')!;
  const gr = g2.createRadialGradient(16, 16, 0, 16, 16, 16);
  gr.addColorStop(0, 'rgba(255,255,220,1)'); gr.addColorStop(0.3, 'rgba(255,236,140,0.85)'); gr.addColorStop(1, 'rgba(255,220,90,0)');
  g2.fillStyle = gr; g2.fillRect(0, 0, 32, 32);
  const tex = new THREE.CanvasTexture(c);
  const geo = new THREE.BufferGeometry();
  const pos = new Float32Array(n * 3);
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  const m = new THREE.PointsMaterial({ map: tex, size: 1.25, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, color: new THREE.Color('#ffe9a0').multiplyScalar(1.8), toneMapped: false });
  const pts = new THREE.Points(geo, m);
  pts.frustumCulled = false; pts.userData.noAO = true;
  const seeds = Array.from({ length: n }, (_, i) => ({ a: (i * 2.399) % (Math.PI * 2), r: 0.8 + ((i * 37) % 23) / 6, t: (i * 0.137) % 1, sp: 0.06 + ((i * 13) % 7) / 90 }));
  const update = (time: number) => {
    seeds.forEach((s, i) => {
      const t = (s.t + time * s.sp) % 1;
      const a = s.a + time * 0.25 + t * 2.0, r = s.r * (1 - t * 0.35);
      pos[i * 3] = EFFIGY[0] + Math.cos(a) * r + Math.sin(time * 1.7 + i) * 0.15;
      pos[i * 3 + 1] = 0.8 + t * 13 + Math.sin(time * 2.3 + i) * 0.2;
      pos[i * 3 + 2] = EFFIGY[1] + Math.sin(a) * r;
    });
    geo.attributes.position.needsUpdate = true;
  };
  return { pts, update };
}

// ---------------------------------------------------------------------------

async function main() {
  injectStyles();
  const fetchAll = () => fetch('/api/commons').then((r) => (r.ok ? r.json() : null)).catch(() => null) as Promise<Commons | null>;
  // (the page's head asked for the Commons already, alongside the bundle: loading.ts)
  let [data, layout, lands] = await Promise.all([early<Commons>('/api/commons'), loadDistrict(), loadLands()]);
  loadStage(2);
  setStyle('A');
  let vw = innerWidth, vh = innerHeight; const dpr = devicePixelRatio || 1;
  if (!lands) throw new Error('the playa and the wilds did not load');
  const city = new PlayaCity(lands.playa), wildsLand = new WildsLand(lands.wilds);
  EFFIGY = city.man;
  PLAYA = { x0: city.man[0] - 36, z0: city.man[1] - 36, x1: city.man[0] + 36, z1: city.man[1] + 36 };
  const prCap = still ? Math.min(2, dpr) : Math.min(1.75, dpr);
  let PR = +(q.get('pr') || (still ? prCap : startPR()));
  const renderer = makeRenderer(vw, vh, PR);
  renderer.info.autoReset = false;
  if (!still) { renderer.shadowMap.autoUpdate = false; renderer.shadowMap.needsUpdate = true; }
  // a plot out in the district is framed the same way, from where its middle is
  const dfp = !PLOTS[focusPlot] && layout ? layout.plots.find((p) => p.n === focusPlot) : null;
  const fp: P | undefined = PLOTS[focusPlot] || (dfp ? [(dfp.rect[0] + dfp.rect[2]) / 2 - 7, (dfp.rect[1] + dfp.rect[3]) / 2 - 5.5] : undefined);
  // ?look=x,z frames any spot on the island (a structure, a building), for stills
  const look = (q.get('look') || '').split(',').map(Number);
  const target = look.length === 2 && look.every(Number.isFinite) ? new THREE.Vector3(look[0], 1.2, look[1])
    : fp ? new THREE.Vector3(fp[0] + 7, 1.2, fp[1] + 5.5) : atPlaya ? city.view().target : atWilds ? wildsLand.view().target : new THREE.Vector3(40, 2.2, 31.5);
  const camDir: [number, number, number] = [-0.5, 0.78, 1.0];
  const { scene, sun, hemi } = makeStage(renderer, { target: new THREE.Vector3(40, 0, 46), shadowExtent: 62, shadowSize: 4096, fogNear: 260, fogFar: 720 });
  // (festival night is at night, world time: the light of the hour shows it, daylight.ts)
  scene.add(clouds(40, -24, 46, 220, 18, 17));

  // ----- the island, the plaza and the paths
  const world = new THREE.Group(); scene.add(world);
  world.add(island(0, 0, 80, 56, { seed: 2, depth: 2.6, stripes: false }));
  // and its lobe past the south-west corner, where Plazmi University stands on its green (campus.ts)
  CAMPUS_GROUNDS.forEach(([x0, z0, x1, z1], i) => world.add(island(x0, z0, x1, z1, { seed: 3 + i, depth: 2.6, stripes: false, coneH: 0.5 })));
  const flag = surf({ kind: 'flagstone', color: '#d6ccba' });
  world.add(wbox(PLAZA.x0, 0.002, PLAZA.z0, PLAZA.x1, 0.03, PLAZA.z1, flag));
  for (const [a, b, c2, d] of [[0, 30.6, PLAZA.x0, 32.6], [PLAZA.x1, 30.6, 80, 32.6], [38.5, PLAZA.z1, 41.5, 56], [38.5, HALL.z1, 41.5, PLAZA.z0]]) world.add(wbox(a, 0.002, b, c2, 0.025, d, flag));
  world.add(at(fountain(), FOUNTAIN[0], 0.02, FOUNTAIN[1]));
  world.add(at(noticeBoard(), PLAZA.x0 + 3.2, 0.02, PLAZA.z0 + 1.2, 0.5));
  const LAMPS: P[] = [[PLAZA.x0 + 1.5, PLAZA.z0 + 1.5], [PLAZA.x1 - 1.5, PLAZA.z0 + 1.5], [PLAZA.x0 + 1.5, PLAZA.z1 - 1.5], [PLAZA.x1 - 1.5, PLAZA.z1 - 1.5], [37.6, 41], [42.4, 41]];
  for (const [x, z] of LAMPS) world.add(at(Y.lampPost({}), x, 0, z));
  for (const [x, z, r] of [[35.5, 36.2, Math.PI], [44.5, 36.2, Math.PI], [35.5, 27.8, 0], [44.5, 27.8, 0]]) world.add(at(Y.gardenBench({}), x, 0, z, r));
  for (const [x, z, k] of [[PLAZA.x0 + 4, 32, 'cherry'], [PLAZA.x1 - 4, 32, 'cherry'], [PLAZA.x0 + 5, PLAZA.z1 - 4, 'oak'], [PLAZA.x1 - 5, PLAZA.z1 - 4, 'oak']] as [number, number, string][]) {
    world.add(at(cyl(1.1, 1.2, 0.5, mat('#c9c0b0', { rough: 0.9 }), 20), x, 0.25, z));
    world.add(at(Y.tree({ kind: k, h: 5.2, seed: Math.floor(x) } as any), x, 0.5, z));
  }
  // (P75) flowerbeds either side of the ways in, never on them: along the plaza's south edge
  // outside the two lamps, and along the hall's front outside its topiaries
  for (const [x, z, w, d, c, sd] of BEDS) world.add(at(Y.flowerBed(w, d, c, sd), x, 0, z));
  // trees and hedges round the edge of the island
  const r = rng(31);
  for (let i = 0; i < 14; i++) {
    const side = i % 4, t = (i + 0.5) / 14;
    const [x, z] = side === 0 ? [2 + t * 76, 1.5] : side === 1 ? [78.5, 2 + t * 52] : side === 2 ? [2 + t * 76, 54.5] : [1.5, 2 + t * 52];
    if (Math.abs(x - 40) < 4 && z > 50) continue;
    if (Math.abs(z - 31.6) < 3) continue;
    if (layout && z < 31 && (x < 4 || x > 76)) continue;
    world.add(at(Y.tree({ kind: ['pine', 'birch', 'oak', 'cherry'][i % 4], h: 4 + r() * 2, seed: i + 7 } as any), x, 0, z));
  }
  // the way to Plazmi University, off past the plaza's south-west corner (campus.ts): a signpost by the path to the playa
  world.add(at(sign([{ text: '↙ Plazmi University', size: 44, color: '#2c3e6b' }, { text: 'the screening room · the green', size: 22, color: '#6e4b33' }], '#f6e9c8', 2.5, 0.75), 36.9, 0, 44.4, -0.35));
  // the way home: a signpost at the west end of the path, and stepping stones off the edge
  world.add(at(sign([{ text: '← the homestead', size: 46, color: '#3a2a20' }], '#f6e9c8', 2.6, 0.6), 2.4, 0, 33.8, Math.PI / 2));
  const stoneM = mat('#86705c', { rough: 1, flat: true });
  for (let k = 1; k <= 5; k++) { const st = sphere(0.9 + (k % 2) * 0.3, stoneM, 7, 5); st.scale.y = 0.45; st.position.set(-3 - k * 3.4, -0.6 - k * 0.5, 31.6 + Math.sin(k) * 1.5); world.add(st); }

  // ----- the Commons windmill: the shared one, its sails turning with the day's wind
  const mill = commonsWindmill();
  const millHub = mill.children.find((o) => o.userData.sails)!;
  mergeStatic(millHub); millHub.userData.keep = true;
  // turned to face the way people look at the plaza from, so the sails read
  mill.position.set(MILL[0], 0, MILL[1]); mill.rotation.y = -0.2;
  world.add(mill);
  world.add(at(sign([{ text: 'THE WINDMILL', size: 50, color: '#b5523b' }, { text: 'take a turn: wind', size: 34, color: '#3a2a20' }], '#f6e9c8', 2.4, 0.85), MILL[0] + 2.9, 0, MILL[1] + 3.3, -0.2));
  for (let i = 0; i < 2; i++) { const st = sphere(0.42, mat("#c9bfae", { rough: 1, flat: true }), 7, 5); st.scale.y = 0.3; st.position.set(MILL[0] + 0.5 + i * 0.25, 0.02, MILL[1] + 3.6 + i * 1.3); world.add(st); }
  const millHit = new THREE.Mesh(new THREE.CylinderGeometry(2.6, 2.6, 13, 8), new THREE.MeshBasicMaterial({ visible: false }));
  millHit.position.set(MILL[0], 6, MILL[1]); millHit.userData.keep = true; millHit.userData.mill = true; world.add(millHit);
  let millSpin = 0;

  // ----- the Dance Hall, with a marquee that says what's on
  const boardMat = new THREE.MeshBasicMaterial({ transparent: true, toneMapped: false });
  const board = new THREE.Mesh(new THREE.PlaneGeometry(8.6, 1.02), boardMat);
  let boardKey = '';
  const setBoard = (title: string, line: string) => {
    if (boardKey === title + line) return; boardKey = title + line;
    boardMat.map?.dispose();
    boardMat.map = letters([{ text: title.toUpperCase(), size: 40, color: '#ff8fb8' }, { text: line, size: 64, color: '#fff3c8' }], 1400, 166, null);
    boardMat.needsUpdate = true;
  };
  const bulbGeo = new THREE.SphereGeometry(0.07, 8, 6);
  const bulbs = [0, 1].map(() => new THREE.InstancedMesh(bulbGeo, new THREE.MeshBasicMaterial({ color: new THREE.Color('#ffe2a0').multiplyScalar(2.4), toneMapped: false }), 40));
  const winMats = [mat('#ffd9a0', { emissive: '#ffb86b', emissiveIntensity: 1.6 }), mat('#ffe2b8', { emissive: '#ffc98a', emissiveIntensity: 1.3 })];
  const hall = hallBuilding(board, bulbs, winMats);
  world.add(hall);
  setBoard('Tonight', 'an open floor, crews welcome');

  // ----- prime plots
  const plotsG = new THREE.Group(); plotsG.userData.keep = true; world.add(plotsG);
  let plotsKey = '';
  const drawPlots = (d: Commons) => {
    const k = JSON.stringify(d.plots);
    if (k === plotsKey) return; plotsKey = k;
    plotsG.clear();
    for (const p of d.plots.plots) {
      const o = PLOTS[p.n]; if (!o) continue;
      const g = splitMaterials(plotLot(p, d.plots.price)); g.position.set(o[0], 0, o[1]); plotsG.add(g); finishShadows(g);
      // the plots merge together (one mesh per material); hovering finds an invisible box
      const hit = new THREE.Mesh(new THREE.BoxGeometry(p.size[0], 4, p.size[1]), new THREE.MeshBasicMaterial({ visible: false }));
      hit.position.set(o[0] + p.size[0] / 2, 2, o[1] + p.size[1] / 2); hit.userData.plot = p; hit.userData.keep = true; plotsG.add(hit);
    }
    plotsG.userData.keep = false; mergeStatic(plotsG); plotsG.userData.keep = true;
    finishShadows(plotsG);
    renderer.shadowMap.needsUpdate = true;
  };

  // ----- the playa, its own island across a bridge: a ring city round the effigy (playacity.ts)
  const playa = new THREE.Group(); scene.add(playa);
  playa.add(city.root);
  const effG = new THREE.Group(); effG.userData.keep = true; effG.position.set(EFFIGY[0], 0, EFFIGY[1]); effG.scale.setScalar(city.manScale); playa.add(effG);
  const swarm = fireflies(); swarm.pts.visible = false; scene.add(swarm.pts);
  const lotsG = new THREE.Group(); lotsG.userData.keep = true; playa.add(lotsG);
  const anim = { spin: [] as THREE.Object3D[], glow: [] as THREE.Material[] };
  const pickables: THREE.Object3D[] = [];
  // lights drawn after the page starts (a new structure, a project's lanterns) join the evening's glow
  let newLights = (_g: THREE.Object3D) => {};
  let playaKey = '', burnUntil = 0, festivalSeen = 0;
  const drawPlaya = (d: Commons) => {
    const pl = d.playa.playa;
    const burning = !!pl.burned && d.now - pl.burned.at < 20 * 60000;
    // (P76) what burned stands burning for the first 8 minutes, then embers, then the lots are bare
    const phase = burning && pl.burned!.ashes?.length ? (d.now - pl.burned!.at < 8 * 60000 ? 'burning' : 'embers') : '';
    city.setTheme(pl.theme?.name);
    const k = JSON.stringify([pl.lots, pl.effigy.planks, burning, phase]);
    if (k === playaKey) return; playaKey = k;
    // on festival night the effigy glows as it stood, then rests bare for the new week
    effG.clear(); const eff = effigyGrown(burning ? Math.max(pl.effigy.planks, pl.burned!.planks) : pl.effigy.planks, burning); effG.add(eff); finishShadows(eff); mergeStatic(eff);
    burnUntil = burning ? performance.now() + 60000 : 0;
    lotsG.clear(); anim.spin.length = 0; anim.glow.length = 0;
    pickables.length = 0;
    for (const l of pl.lots) {
      // the city draws every lot's stakes and number; here, what's on it
      if (!l.camp && !l.pieces.length && !l.builds?.length) continue;
      const g = new THREE.Group(); if (!city.placeLot(g, l.n)) continue;
      if (l.camp) { const ban = campBanner(l.camp, l.size[0], l.size[1]); g.add(ban); finishShadows(ban); mergeStatic(ban); }
      for (const p of l.pieces) { const m = playaPiece(p, anim); m.position.set(p.at[0], 0, p.at[1]); g.add(m); pickables.push(m); finishShadows(m); mergeStatic(m); }
      // what a crew raised there, or is raising (playa-builds.ts)
      for (const b of l.builds || []) { const m = playaBuild(b); m.position.set(b.at[0], 0, b.at[1]); g.add(m); pickables.push(m); mergeStatic(m); }
      lotsG.add(g);
    }
    if (phase) for (const a of pl.burned!.ashes!) {
      const g = new THREE.Group(); if (!city.placeLot(g, a.lot)) continue;
      const m = burningThing(a, phase, anim.glow, (x) => playaPiece({ id: x.id, kind: x.kind, colour: x.colour || '#a87c4f' })); m.position.set(a.at[0], 0, a.at[1]); g.add(m); lotsG.add(g);
    }
    finishShadows(playa);
    newLights(lotsG);
    renderer.shadowMap.needsUpdate = true;
  };

  // ----- the market: a row of stalls each side of the hall's carpet, facing the fountain
  const marketG = new THREE.Group(); marketG.userData.keep = true; world.add(marketG);
  const stallHits: THREE.Object3D[] = [];
  let marketKey = '';
  const drawMarket = (d: Commons) => {
    const stalls = d.market?.stalls || [];
    const k = JSON.stringify(stalls.slice(0, STALLS.length));
    if (k === marketKey) return; marketKey = k;
    marketG.clear(); stallHits.length = 0;
    STALLS.forEach(([x, z], i) => {
      const s = stalls[i];
      const m = s ? stallModel(s) : emptyStall(i);
      m.position.set(x, 0, z); marketG.add(m); finishShadows(m); mergeStatic(m);
      if (s) stallHits.push(m);
    });
    renderer.shadowMap.needsUpdate = true;
  };

  // ----- the pedlar's cart: by the fountain on Wednesdays (cart.ts)
  const cartG = new THREE.Group(); cartG.userData.keep = true; world.add(cartG);
  let cartKey = '';
  const drawCart = (d: Commons) => {
    const k = JSON.stringify(d.cart?.here ? d.cart.things?.map((t) => t.id) : null);
    if (k === cartKey) return; cartKey = k;
    cartG.clear();
    if (d.cart?.here) { const m = cartModel(d.cart); m.position.set(CART_AT[0], 0, CART_AT[1]); m.rotation.y = 0.12; m.scale.setScalar(1.2); cartG.add(m); finishShadows(m); }
    renderer.shadowMap.needsUpdate = true;
  };

  // ----- the wilds: their own island past the plaza, over a plank bridge
  const wilds = new THREE.Group(); scene.add(wilds);
  wilds.add(wildsLand.root);
  // the way there: a signpost at the plaza's east end
  world.add(at(sign([{ text: 'the wilds →', size: 46, color: '#3a2a20' }, { text: 'woodlot · quarry · meadow · scrapyard', size: 22, color: '#6e4b33' }], '#f6e9c8', 2.8, 0.75), 77.6, 0, 29.6, -Math.PI / 2));
  const placeHits: THREE.Object3D[] = wildsLand.hits;

  // ----- the town round the plaza: seven buildings with live boards, and the projects standing here
  const town = new Town();
  world.add(town.root);
  const district = layout ? new District(layout) : null;
  if (district) scene.add(district.root);

  if (data) { drawPlots(data); drawPlaya(data); drawMarket(data); drawCart(data); town.update(data.town, data.market); district?.update(data.plots); }
  finishShadows(world);
  // the hall is clicked as a whole, so it keeps its own merged meshes
  mergeStatic(hall); hall.userData.keep = true;
  // (P83) merged by cells of 48 m and instanced meshes split the same way, so what's beyond the view or off screen isn't drawn
  mergeStatic(world, { cell: 96 }); mergeStatic(playa, { cell: 96 }); mergeStatic(wilds, { cell: 96 });
  const farDetail = new FarDetail();
  for (const g of [world, playa, wilds]) { chunkInstanced(g); farDetail.add(g); }

  // ----- the plazmi here: on the plaza, and out on the playa
  const fx = new Fx(); scene.add(fx.root);
  const plazaFloor = floorHouse(PLAZA.x1 - PLAZA.x0, PLAZA.z1 - PLAZA.z0,
    [{ id: 'fountain', x: FOUNTAIN[0] - PLAZA.x0 - 2, y: FOUNTAIN[1] - PLAZA.z0 - 2, w: 4, d: 4, level: 0 },
      // the market's stalls, which plazmi walk round
      ...STALLS.map(([x, z], i) => ({ id: `stall${i}`, x: x - 1.2 - PLAZA.x0, y: z - 0.65 - PLAZA.z0, w: 2.4, d: 1.05, level: 0 })),
      // the café's tables (town.ts)
      // (P89: just the table, so a plazmi can take a chair at it)
      ...PLAZA_THINGS.map((t, i) => ({ id: `cafe${i}`, x: t.x + t.w / 2 - 0.4 - PLAZA.x0, y: t.z + t.d / 2 - 0.4 - PLAZA.z0, w: 0.8, d: 0.8, level: 0 })),
      // the pedlar's cart, when it's here (cart.ts)
      ...(data?.cart?.here ? [{ id: 'cart', x: CART_AT[0] - CART_FOOT.w / 2 - PLAZA.x0, y: CART_AT[1] - CART_FOOT.d / 2 - PLAZA.z0, w: CART_FOOT.w, d: CART_FOOT.d, level: 0 }] : []),
      // (P89) the trees' planters, which they walk round rather than through
      ...([[30, 32], [50, 32], [31, 38], [49, 38]] as P[]).map(([x, z], i) => ({ id: `tree${i}`, x: x - 1.2 - PLAZA.x0, y: z - 1.2 - PLAZA.z0, w: 2.4, d: 2.4, level: 0 })),
      // the flowerbeds on the plaza, which they walk round too
      ...BEDS.filter(([, z]) => z > PLAZA.z0 && z < PLAZA.z1).map(([x, z, w, d], i) => ({ id: `bed${i}`, x: x - w / 2 - PLAZA.x0, y: z - d / 2 - PLAZA.z0, w, d, level: 0 }))],
    [[35.5, 36.2], [44.5, 36.2], [35.5, 27.8], [44.5, 27.8]].map(([x, z], i) => ({ id: `b${i}`, at: [x - PLAZA.x0, z - PLAZA.z0], render: 'garden-bench', facing: i < 2 ? 'north' : 'south' })) as any);
  // (P89) the plaza when nothing's on: where plazmi gather (round the fountain, in the open paving), where
  // they sit (the benches, the fountain's rim, the café's chairs), and the ways kept clear. (The spots
  // are where the trees don't hide them from the page's camera, which looks from the south-west.) (to the hall's
  // doors, in from the south and from the walks east and west, the front of the stalls); all world tiles
  const plazaIdle = (): IdleGround => {
    const L = (x: number, z: number): P => [x - PLAZA.x0, z - PLAZA.z0];
    const R = (x0: number, z0: number, x1: number, z1: number) => ({ x0: x0 - PLAZA.x0, z0: z0 - PLAZA.z0, x1: x1 - PLAZA.x0, z1: z1 - PLAZA.z0 });
    const F = FOUNTAIN, cart = !!data?.cart?.here;
    const sites = ([[46, 32], [36, 31], [35.2, 38.8], [44.8, 38.9], [48.5, 28]] as P[])
      .filter(([x, z]) => !cart || Math.abs(x - CART_AT[0]) > 3.2 || Math.abs(z - CART_AT[1]) > 2.4)
      .map(([x, z]) => ({ at: L(x, z), view: L(F[0], F[1]) }));
    const seats: Seat[] = [];
    [[35.5, 36.2, -1], [44.5, 36.2, -1], [35.5, 27.8, 1], [44.5, 27.8, 1]].forEach(([x, z, f], i) => {
      if (cart && i === 2) return; // the cart stands in front of it on Wednesdays
      // (one to a bench: two plazmi are wider than it)
      seats.push({ id: `b${i}`, at: L(x, z + f * 0.2), face: L(x, z + f * 3), top: 0.48, from: L(x, z + f * 0.95) });
    });
    // the fountain's rim, east and west, looking out
    for (const [side, a0] of [['e', 0], ['w', Math.PI]] as [string, number][]) for (const da of [-0.3, 0.3]) {
      const a = a0 + da, ux = Math.cos(a), uz = Math.sin(a);
      seats.push({ id: `rim${side}${da}`, pair: `rim${side}`, rank: 2, at: L(F[0] + ux * 2.55, F[1] + uz * 2.55), face: L(F[0] + ux * 8, F[1] + uz * 8), top: 0.55, from: L(F[0] + ux * 3.2, F[1] + uz * 3.2) });
    }
    // the café's two tables: the chairs on the plaza side
    PLAZA_THINGS.forEach((t, i) => { const cx = t.x + t.w / 2, cz = t.z + t.d / 2; seats.push({ id: `cafe${i}`, pair: 'cafe', rank: 1, at: L(cx - 0.62, cz), face: L(cx, cz), top: 0.46, from: L(cx - 1.45, cz) }); });
    return {
      place: 'plaza', sites, seats,
      lanes: [R(38.2, 22, 41.8, 29.6), R(38.2, 34.4, 41.8, 42), R(26, 30.4, 28.6, 32.8), R(51.4, 30.4, 54, 32.8), R(29.5, 24.9, 50.5, 26.1),
        // (and out from under the trees, where they'd stand in the planters or be hidden by the leaves)
        ...([[30, 32], [50, 32], [31, 38], [49, 38]] as P[]).map(([x, z]) => R(x - 2.6, z - 2.6, x + 2.6, z + 2.6)),
        // and off the lamp posts
        ...LAMPS.map(([x, z]) => R(x - 0.6, z - 0.6, x + 0.6, z + 0.6))],
    };
  };
  const plazaStart = (i: number): P => [[10.5, 13], [17.5, 13], [8, 15], [20, 15], [14, 16.5], [6, 12], [22, 12], [11, 17.5], [17, 17.5]][i % 9] as P;
  const plaza = new Troupe(plazaFloor, ui, fx, {
    party: 0.15, start: plazaStart, busy: (l) => jobs.has(l),
    // a ring on the paving, wide enough for everyone in it a body and a half apart
    formation: (slugs) => {
      const n = Math.max(1, slugs.length), rz = Math.max(1.8, 1.6 / (2 * Math.sin(Math.PI / Math.max(2, n)))), rx = Math.max(3.6, rz * 1.7);
      return { spots: slugs.map((_, i) => { const a = Math.PI / 2 + (i / n) * Math.PI * 2; return [14 + Math.cos(a) * rx, 15 + Math.sin(a) * rz] as P; }), face: [0, 1] };
    },
    ground: () => 0.03, // the paving
    audience: (i) => [8 + i * 1.5, 18.5] as P,
    idle: plazaIdle(),
    murmurs: ['what a fountain', 'hello, plaza', 'hm hm ♪', 'nice day for it', 'is that the hall?', 'ooh, a bench'],
  });
  plaza.root.position.set(PLAZA.x0, 0, PLAZA.z0); scene.add(plaza.root);
  // round the effigy on its deck, in the open circle of the playa
  const EC = EFFIGY[0] - PLAYA.x0, ED = 3.6 * city.manScale;
  const playaFloor = floorHouse(PLAYA.x1 - PLAYA.x0, PLAYA.z1 - PLAYA.z0, [{ id: 'effigy', x: EC - ED, y: EC - ED, w: ED * 2, d: ED * 2, level: 0 }]);
  const toMan = city.dir(6);
  // (P89) the playa when nothing's on: little groups on the open sand round the effigy, looking at it,
  // with the way in from the bridge (6:00) kept clear
  const playaIdle = (): IdleGround => {
    const a6 = Math.atan2(toMan[1], toMan[0]), sites: { at: P; view: P }[] = [], lanes = [];
    for (let k = 1; k < 9; k++) { const a = a6 + (k / 9) * Math.PI * 2, r = ED + 5.5 + (k % 3) * 1.4; sites.push({ at: [EC + Math.cos(a) * r, EC + Math.sin(a) * r], view: [EC, EC] }); }
    for (let r = ED + 1; r < ED + 16; r += 1.5) { const x = EC + Math.cos(a6) * r, z = EC + Math.sin(a6) * r; lanes.push({ x0: x - 1.3, z0: z - 1.3, x1: x + 1.3, z1: z + 1.3 }); }
    return { place: 'playa', sites, seats: [], lanes };
  };
  const playaCrowd = new Troupe(playaFloor, ui, fx, {
    party: 0.4, busy: (l) => jobs.has(l), start: (i) => { const a = Math.atan2(toMan[1], toMan[0]) + ((i % 7) - 3) * 0.32, d = ED + 5 + (i % 2) * 2; return [EC + Math.cos(a) * d, EC + Math.sin(a) * d] as P; },
    formation: (slugs) => ({ spots: slugs.map((_, i) => { const a = (i / Math.max(1, slugs.length)) * Math.PI * 2; return [EC + Math.cos(a) * (ED + 3.2), EC + Math.sin(a) * (ED + 3.2)] as P; }), face: [0, 1] }),
    audience: (i) => [EC - 8 + i * 1.6, EC + ED + 9] as P,
    idle: playaIdle(),
    murmurs: ['what shall I build?', 'ooh, lanterns', 'for the effigy ♪', 'sand in my antenna', 'hee', 'camp vibes'],
  });
  playaCrowd.root.position.set(PLAYA.x0, 0, PLAYA.z0); scene.add(playaCrowd.root);
  // in the wilds, everyone at its work: a troupe for each place, standing where the gathering is
  const gatherers: Record<string, Troupe> = {};
  const MURMURS: Record<string, string[]> = {
    woodlot: ['one more for the pile', 'hm hm ♪', 'what a log', 'smells of pine'], quarry: ['clink, clink', 'good stone, this', 'hm hm ♪', 'mind the clay'],
    meadow: ['hello, sheep', 'so much flax', 'hm hm ♪', 'the stream is cold'], scrapyard: ['ooh, copper', 'clank', 'hm hm ♪', 'this could be a wheel'],
    'clay-pit': ['squelch', 'good clay, this', 'hm hm ♪', 'the stream is cold'], flax: ['so much flax', 'so blue', 'hm hm ♪', 'a good row'],
    sheep: ['hello, sheep', 'so soft', 'hm hm ♪', 'baa to you too'], orchard: ['an apple!', 'hm hm ♪', 'seeds everywhere', 'hello, bees'],
  };
  // a troupe for each gathering ground (wildsland.ts): a plazmi gathering clay works at the clay pit, wool in the sheep meadow
  const groundPlace: Record<string, string> = {};
  for (const gr of wildsLand.grounds) {
    const [x0, z0, x1, z1] = gr.rect;
    const spot = (i: number) => { const w = gr.work[i % gr.work.length]; return [w[0] - x0, w[1] - z0] as P; };
    // (a floor wants something on it: a stone in the corner)
    const t = new Troupe(floorHouse(x1 - x0, z1 - z0, [{ id: `${gr.id}-0`, x: 0.5, y: 0.5, w: 1, d: 1, level: 0 }]), ui, fx, {
      party: 0, start: spot,
      formation: (slugs) => ({ spots: slugs.map((_, i) => spot(i)), face: [0, 1] }),
      audience: (i) => [2 + i * 1.4, z1 - z0 - 2] as P,
      murmurs: MURMURS[gr.id] || MURMURS[gr.place],
    });
    t.root.position.set(x0, 0, z0); scene.add(t.root);
    gatherers[gr.id] = t; groundPlace[gr.id] = gr.place;
  }
  // (P51) a crew raising a build works at its lot out in the ring city, past the crowd's floor round the effigy:
  // a little floor of its own round that lot, which the crew walks onto from the crowd
  const CREW_R = 9;
  const lotPt = (n: number, lx: number, lz: number): P => { const v = new THREE.Vector3(lx, 0, lz).applyMatrix4(city.lotMatrix(n)); return [v.x, v.z]; };
  const crews = new Map<number, Troupe>();
  const crewFor = (n: number) => {
    let t = crews.get(n); if (t) return t;
    const l = city.lot(n)!, o: P = [l.at[0] - CREW_R, l.at[1] - CREW_R];
    const start = (i: number): P => { const w = lotPt(n, 4.5 + ((i % 3) - 1) * 1.8, 8.4 + Math.floor(i / 3) * 1.2); return [w[0] - o[0], w[1] - o[1]]; };
    t = new Troupe(floorHouse(CREW_R * 2, CREW_R * 2, [{ id: `lot${n}`, x: 0.3, y: 0.3, w: 0.6, d: 0.6, level: 0 }]), ui, fx, {
      party: 0, start, formation: (s) => ({ spots: s.map((_, i) => start(i)), face: [0, 1] }), audience: (i) => start(i + 3),
      murmurs: ['up she goes', 'hand me a plank', 'hm hm ♪', 'nearly there', 'mind your antenna'],
    });
    t.root.position.set(o[0], 0, o[1]); scene.add(t.root);
    crews.set(n, t); gatherers[`lot${n}`] = t;
    return t;
  };
  // who's on a crew: present on the playa, and building something not yet up
  const hearCrews = (d: Commons) => {
    const crewOf = new Map<string, number>(), here = new Set(d.playa.present.map((g) => g.slug));
    for (const l of d.playa.playa.lots) for (const b of l.builds || []) {
      if (b.up || !city.lot(l.n)) continue;
      for (const s of b.builders.map((x) => x.slug).concat(b.by)) if (here.has(s) && !crewOf.has(s)) crewOf.set(s, l.n);
    }
    for (const n of new Set(crewOf.values())) crewFor(n);
    playaCrowd.hear({ ...d.playa, present: d.playa.present.filter((g) => !crewOf.has(g.slug)) });
    for (const s of crewOf.keys()) { const dn = playaCrowd.dancers.get(s); if (dn) dn.life.gone = true; }
    for (const [n, t] of crews) t.hear({ ...d.playa, present: d.playa.present.filter((g) => crewOf.get(g.slug) === n), showing: null, chat: [] });
  };
  const atStall = new Set<string>();
  // (P36b) whoever's just turned the windmill stands at its crank a while, pushing it round:
  // a little floor of its own by the mill's door, which the turner walks onto from the plaza
  const MILL_MINUTES = 4;
  const MILL_FLOOR = { x0: MILL[0] - 4, z0: MILL[1] - 1, w: 9, d: 7 };
  // round the mill's south-west foot, where the page's camera sees them (the post office hides its south;
  // the prime plot lies west)
  const MILL_SPOTS: P[] = [[19.4, 21.0], [20.1, 20.0], [19.0, 22.2], [20.6, 21.9]];
  const millCrank = (i: number): P => [MILL_SPOTS[i % 4][0] - MILL_FLOOR.x0, MILL_SPOTS[i % 4][1] - MILL_FLOOR.z0];
  const millCrew = new Troupe(floorHouse(MILL_FLOOR.w, MILL_FLOOR.d, [{ id: 'mill', x: 2.2, y: -1.2, w: 3.8, d: 3.6, level: 0 }]), ui, fx, {
    party: 0, start: millCrank, formation: (s) => ({ spots: s.map((_, i) => millCrank(i)), face: [0, 1] }), audience: (i) => millCrank(i + 2),
    murmurs: ['round and round', 'hup, hup', 'there go the sails', 'hm hm ♪'],
  });
  millCrew.root.position.set(MILL_FLOOR.x0, 0, MILL_FLOOR.z0); scene.add(millCrew.root);
  gatherers.mill = millCrew;
  // what everyone's at (placework.ts), and what the pages saw last, to notice a sale, a chip-in, a plank
  const jobs = new Jobs(fx, (l) => { for (const t of Object.values(gatherers).concat(plaza, playaCrowd)) if (t.root === l.root.parent) return t.at(l); return l.head; });
  const seen = { items: new Map<string, number>(), gave: new Map<string, string>(), planks: -1 };
  // side-on to the camera, so the tool and the work both show
  const side = (p: P): P => [p[0] + 2.7, p[1] + 1.35];
  // in the wilds: how each material is gathered, and what's carried to the ground's heap (where is the ground's: wildsland.ts)
  const GATHER: Record<string, { act: string[]; secs: number[]; emoji: string[]; carry?: string }> = {
    timber: { act: ['gather-timber'], secs: [7.5], emoji: ['🪓'], carry: 'log' },
    sticks: { act: ['gather-sticks', 'gather-leaves'], secs: [6.8, 3.9], emoji: ['🌿', '🍂'], carry: 'sticks' },
    stone: { act: ['gather-stone'], secs: [6], emoji: ['⛏️'], carry: 'stone' },
    clay: { act: ['gather-clay'], secs: [7.2], emoji: ['🏺'], carry: 'clay' },
    sand: { act: ['gather-sand'], secs: [6.4], emoji: ['🏖️'], carry: 'pail' },
    flax: { act: ['gather-flax'], secs: [8], emoji: ['🌾'] },
    wool: { act: ['gather-wool'], secs: [9.6], emoji: ['🐑'] },
    flowers: { act: ['gather-flowers', 'gather-seeds'], secs: [6.6, 4.2], emoji: ['🌸', '🌱'] },
    scrap: { act: ['gather-scrap'], secs: [8.8], emoji: ['⚙️'] },
  };
  const KIND_MATERIAL: Record<string, string> = { wood: 'timber', rock: 'stone', clay: 'clay', flax: 'flax', pasture: 'wool', orchard: 'flowers', scrap: 'scrap' };
  // the steps of gathering at a ground: work at its spot, then (for what's carried) over to its heap and set it down
  const gatherSteps = (gr: Ground, material: string, i: number): Step[] => {
    const g = GATHER[material] || GATHER[['iron', 'tin', 'copper'].includes(material) ? 'scrap' : KIND_MATERIAL[gr.kind] || 'scrap'];
    const w = workSpots(gr, material), L = (q: P): P => [q[0] - gr.rect[0], q[1] - gr.rect[1]];
    const spot = L(w.spots[i % w.spots.length]);
    const label = `gathering ${material}`;
    const steps: Step[] = g.act.map((a, k) => ({ action: a, secs: g.secs[k], at: spot, face: side(spot), label, emoji: g.emoji[k] }));
    if (g.carry && w.heap) { const h = L(w.heap); steps.push({ action: `drop-${g.carry}`, secs: 1.2, at: h, face: [h[0], h[1] + 1], carry: `carry-${g.carry}`, label: `carrying ${material} to the heap` }); }
    return steps;
  };
  // who's at work, from the poll
  const assignJobs = (d: Commons) => {
    const busy = new Set<Life>();
    // the wilds: each at its material, at the ground its troupe stands on
    for (const [id, t] of Object.entries(gatherers)) {
      const gr = wildsLand.ground(id), w = gr && d.wilds?.[groundPlace[id]]; if (!gr || !w) continue;
      const count = new Map<string, number>();
      for (const g of w.gathering) {
        const dn = t.dancers.get(g.slug); if (!dn) continue;
        const k = count.get(g.material) || 0; count.set(g.material, k + 1);
        jobs.set(dn.life, `${id}:${g.material}:${k}`, gatherSteps(gr, g.material, k)); busy.add(dn.life);
      }
    }
    // the market: each seller minds its stall, and bows to a sale
    (d.market?.stalls || []).slice(0, STALLS.length).forEach((s, i) => {
      const dn = plaza.dancers.get(s.seller); if (!dn) return;
      // beside its stall's front corner, out from under the awning, where it's seen setting things out
      const at: P = [STALLS[i][0] - PLAZA.x0 - 1.3, STALLS[i][1] + 0.15 - PLAZA.z0];
      jobs.set(dn.life, `stall${i}`, [{ action: 'stall-tend', secs: 10.2, at, face: [at[0], at[1] + 3], label: 'minding its stall', emoji: '🧺' }]);
      busy.add(dn.life);
      const n = s.items.length, was = seen.items.get(s.seller);
      if (was !== undefined && n < was) jobs.once(dn.life, [{ action: 'stall-sold', secs: 2.6, at, face: [at[0], at[1] + 3], label: 'selling something', emoji: '🪙' }]);
      seen.items.set(s.seller, n);
    });
    // the windmill: at the crank
    millCrew.list.forEach((dn, i) => {
      const at = millCrank(i);
      jobs.set(dn.life, `mill${i}`, [{ action: 'crank', secs: 9, at, face: [MILL[0] - MILL_FLOOR.x0, MILL[1] - MILL_FLOOR.z0], label: 'turning the windmill', emoji: '🌬️' }]);
      busy.add(dn.life);
    });
    // the playa: a crew raising a build hammers, fetches planks and heaves beams up together; a chip-in tips a basket out
    const pl = d.playa.playa;
    for (const l of pl.lots) for (const b of l.builds || []) {
      if (b.up) continue;
      // (P51) in the lot's own frame (playacity.ts), turned to face its street: the crew stands along the build's front
      const t = crews.get(l.n); if (!t) continue;
      const W = (lx: number, lz: number): P => { const w = lotPt(l.n, lx, lz); return [w[0] - t.root.position.x, w[1] - t.root.position.z]; };
      const here = b.builders.map((x) => x.slug).concat(b.by).filter((s, i, a) => a.indexOf(s) === i && t.dancers.has(s));
      here.forEach((slug, k) => {
        const dn = t.dancers.get(slug)!;
        const off = k - (here.length - 1) / 2, out = off < 0 ? -1 : 1;
        const lx = b.at[0] + off * 1.8, lz = b.at[1] + b.size[1] / 2 + 0.9;
        const spot = W(lx, lz), face = W(lx, lz - 3);
        const label = `raising the ${b.name}`;
        // hammering in turns (every other one fetches a plank first), then the beam up all together
        const hammer: Step = { action: 'build-hammer', secs: 4.4, at: spot, face, label, emoji: '🔨' };
        // (its planks set down on its own outer side, clear of its neighbour)
        const plank: Step = { action: 'drop-plank', secs: 1.2, at: W(lx + out * 1.4, lz + 0.9), face, carry: 'carry-plank', label };
        const beam: Step = { action: 'raise-beam', secs: 5.2, at: spot, face: W(lx, lz + 3), label, emoji: '💪', together: `beam:${b.id}`, fill: { ...hammer, secs: 2.2, emoji: undefined } };
        jobs.set(dn.life, `build:${b.id}:${k}`, k % 2 ? [plank, hammer, beam] : [hammer, plank, beam]);
        busy.add(dn.life);
        const gave = JSON.stringify(b.builders.find((x) => x.slug === slug)?.gave ?? null), key = `${b.id}:${slug}`, was = seen.gave.get(key);
        if (was !== undefined && was !== gave) jobs.once(dn.life, [{ action: 'chip-in', secs: 2.4, at: spot, face, label: `chipping in for the ${b.name}`, emoji: '🧺' }]);
        seen.gave.set(key, gave);
      });
    }
    // the effigy: whoever just added a plank carries it up and fixes it on, then claps
    if (seen.planks >= 0 && pl.effigy.planks > seen.planks) {
      const who = pl.effigy.latest[pl.effigy.latest.length - 1]?.who;
      const dn = playaCrowd.list.find((x) => x.g.name === who);
      if (dn) { const at: P = [EC + 0.6, EC + ED + 0.8]; jobs.once(dn.life, [{ action: 'add-plank', secs: 2.8, at, face: [at[0] - 0.6, at[1] - 4], carry: 'carry-plank', label: 'adding a plank to the effigy', emoji: '🪵' }]); busy.add(dn.life); }
    }
    seen.planks = pl.effigy.planks;
    jobs.keepOnly(busy);
  };

  // visitors at the plaza: toy figures by the fountain
  const people = new THREE.Group(); scene.add(people);
  let peopleKey = '';
  const placePeople = (l: PlaceLive) => {
    const key = l.visitors.map((v) => v.id).join(',');
    if (key === peopleKey) return; peopleKey = key;
    people.clear();
    l.visitors.slice(0, 10).forEach((v, i) => {
      const a = 2.2 + i * 0.45;
      const fig = at(toyFigure(v.look as any), FOUNTAIN[0] + Math.cos(a) * 3.4, 0, FOUNTAIN[1] + Math.sin(a) * 3.4, -a - Math.PI / 2);
      fig.userData.visitor = v; people.add(fig);
    });
    finishShadows(people); renderer.shadowMap.needsUpdate = true;
  };
  // (P70) walking, you're the walker: your own figure by the fountain is left out
  const minePeople = () => {
    const walking = !!walkerNow();
    for (const fig of people.children) {
      const vis = !(walking && isMe(fig.userData.visitor));
      if (fig.visible !== vis) { fig.visible = vis; renderer.shadowMap.needsUpdate = true; }
    }
  };

  // ----- the page
  // where you are: the shell's label (its live line is `sub`), set up once the page is live
  const sub = el('span', '', 'where humans arrive');
  const mine = fp ? data?.plots.plots.find((p) => p.n === focusPlot) : null;
  const placeName = mine?.owner_name ? `${mine.owner_name}'s plot` : atPlaya ? 'The playa' : atWilds ? 'The wilds' : 'The Commons';
  // what's on here: the dock's first tab (filled by drawHud), made once the page is live
  const hud = el('div');
  let hudPeek = '';
  let hudKey = '';
  const drawHud = (d: Commons) => {
    const here = atPlaya ? d.playa : d.plaza, pl = d.playa.playa;
    const k = JSON.stringify([here.present.map((g) => g.slug), d.hall, d.plots, pl.lots.map((l) => [l.camp, l.pieces.length]), pl.effigy, here.visitors.length,
      d.market, d.cart, d.windmill, d.town, Object.values(d.wilds || {}).map((w) => [w.present.map((g) => g.slug), w.gathering])]);
    if (k === hudKey) return; hudKey = k;
    hud.innerHTML = '';
    const sec = (title: string) => { const s = el('section'); s.append(el('h4', '', title)); hud.append(s); return s; };
    if (atWilds) {
      for (const w of Object.values(d.wilds || {})) {
        const s = sec(`${w.name.replace(/^the /, 'The ')} · ${w.present.length}`);
        const busy = w.gathering.map((g) => `${g.name}, gathering ${g.material}`);
        s.append(el('p', busy.length ? '' : 'quiet', busy.length ? `${busy.join('; ')}.` : `Nobody gathering just now. There’s ${w.gives.join(', ')} to be had here.`));
      }
      return;
    }
    const s1 = sec(atPlaya ? `On the playa · ${here.present.length}` : `On the plaza · ${here.present.length}`);
    const ul = el('ul', 'who');
    for (const g of here.present) { const li = el('li'); const dot = el('i'); dot.style.background = g.color; li.append(dot, el('span', '', `${g.name} ${g.emoji || ''}`.trim())); if (g.crew) li.append(el('em', '', g.crew.name)); ul.append(li); }
    if (!here.present.length) ul.append(el('li', 'quiet', atPlaya ? 'Nobody out here just now.' : 'Nobody on the plaza just now.'));
    s1.append(ul);
    if (here.visitors.length) s1.append(el('p', 'quiet', `Visiting: ${here.visitors.map((v) => v.name).join(', ')}`));
    const s2 = sec('The Dance Hall');
    s2.append(el('p', '', `${d.hall.marquee.title}: ${d.hall.marquee.line}.`));
    s2.append(el('p', 'quiet', d.hall.inside ? `${d.hall.inside} inside.` : 'Quiet just now.'));
    if (atPlaya) {
      const s3 = sec('The playa');
      const camps = pl.lots.filter((l) => l.camp);
      s3.append(el('p', '', `${pl.lots.reduce((a, l) => a + l.pieces.length, 0)} pieces up on ${pl.lots.filter((l) => l.pieces.length).length} of ${pl.lots.length} lots.${camps.length ? ` Camps: ${camps.map((l) => `${l.camp!.name} (lot ${l.n})`).join(', ')}.` : ''}`));
      s3.append(el('p', '', `The effigy holds ${pl.effigy.planks} plank${pl.effigy.planks === 1 ? '' : 's'}.${pl.effigy.latest.length ? ` Latest, from ${pl.effigy.latest[pl.effigy.latest.length - 1].who}: “${pl.effigy.latest[pl.effigy.latest.length - 1].text}”` : ''}`));
      // what crews have raised, or are raising (playa-builds.ts)
      const bs = pl.lots.flatMap((l) => (l.builds || []).map((b) => ({ b, n: l.n })));
      if (bs.length) s3.append(el('p', '', bs.map(({ b, n }) => `The ${b.name} on lot ${n}: ${b.up ? raisedBy(b) : `going up, ${Math.round(b.progress * 100)}% there`}.`).join(' ')));
      else s3.append(el('p', 'quiet', 'Nothing raised yet. Crews raise domes, towers, stages and temples here together.'));
      if (pl.theme) s3.append(el('p', '', `This week's theme: ${pl.theme.name}.`));
      s3.append(el('p', 'quiet', 'Festival night is Saturday evening. Everything on the playa burns; nothing comes back but the stories, the glow and a ribbon for the favourites.'));
    } else {
      // the pedlar's cart: what's on it today, or quietly when it comes
      const ct = d.cart;
      if (ct?.here) {
        const sc = sec("The pedlar's cart");
        const things = ct.things || [];
        sc.append(el('p', '', `${ct.line} On it: ${things.map((t) => `${t.name.toLowerCase()} (${t.coins} coins${t.cart_only ? ', only from the cart' : ''})`).join(', ')}.`));
        const gone = things.reduce((a, t) => a + t.gone_home, 0);
        sc.append(el('p', 'quiet', gone ? `${gone} thing${gone === 1 ? '' : 's'} gone home with residents today. One of each to a resident.` : 'One of each to a resident.'));
      }
      const mk = d.market;
      if (mk) {
        const sm = sec('The market');
        const things = mk.stalls.reduce((a, s) => a + s.items.length, 0);
        sm.append(el('p', '', mk.stalls.length ? `${mk.stalls.length} stall${mk.stalls.length === 1 ? '' : 's'} out, ${things} thing${things === 1 ? '' : 's'} for sale: ${mk.stalls.map((s) => `${s.name}'s`).join(', ')}.` : 'The stalls stand empty just now.'));
        if (mk.lately) sm.append(el('p', 'quiet', `Lately: ${mk.lately}.`));
        if (mk.limited) sm.append(el('p', 'quiet', `This season's limited piece: the ${mk.limited.name.toLowerCase()}.`));
      }
      // the town round the plaza (town.ts): what each building's board says, in a line or two
      const tw = d.town;
      if (tw) {
        const st = sec('The town');
        const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;
        st.append(el('p', '', `${n(tw.post.today, 'postcard', 'postcards')} sent today, ${n(tw.bakery.today, 'dish', 'dishes')} from the ovens, ${n(tw.workshop.today, 'thing', 'things')} made at the Workshop.`));
        if (tw.gazette.headline) st.append(el('p', 'quiet', `The Gazette: ${tw.gazette.headline}.`));
        const built = tw.projects.filter((p) => p.state === 'built'), going = tw.projects.filter((p) => p.state !== 'built');
        if (built.length || going.length) st.append(el('p', 'quiet', [built.length ? `Built together: ${built.map((p) => p.name || p.title).join(', ')}.` : '', going.length ? `Going up: ${going.map((p) => `${p.name || p.title} (${Math.round(p.progress * 100)}%)`).join(', ')}.` : ''].filter(Boolean).join(' ')));
      }
      const s3 = sec('Plots on the Commons');
      const forSale = d.plots.counts?.['for-sale'] ?? d.plots.plots.filter((p) => p.status === 'for-sale').length, owned = d.plots.plots.filter((p) => p.status === 'owned');
      const pr = d.plots.prices;
      s3.append(el('p', '', `${forSale} for sale${forSale ? `, from ${pr ? Math.min(pr.outer, pr.inner) : d.plots.price} coins` : ''}${owned.length ? `; ${owned.map((p) => `plot ${p.n} is ${p.owner_name}'s`).join(', ')}` : ''}.${d.plots.open_line ? ` ${d.plots.open_line}` : ''}`));
      s3.append(el('p', 'quiet', 'The rest are reserved: the crown opens them quarter by quarter.'));
      const wm = d.windmill;
      if (wm) {
        const sw = sec('The windmill');
        sw.append(el('p', '', `${wm.weather} ${wm.turns.length ? `Turned ${wm.turns.length} time${wm.turns.length === 1 ? '' : 's'} today, by ${wm.turns.map((t) => t.name).join(', ')}.` : 'Nobody has taken a turn yet today.'}`));
        sw.append(el('p', 'quiet', 'Plazmi take turns at the sails. Each turn fills their jars a little, and sends a little to whoever turned it earlier.'));
      }
      const s4 = sec('The playa');
      const nc = pl.lots.filter((l) => l.camp).length, np = pl.lots.reduce((a, l) => a + l.pieces.length, 0);
      s4.append(el('p', '', `${nc} camp${nc === 1 ? '' : 's'}, ${np} piece${np === 1 ? '' : 's'}, ${pl.effigy.planks} plank${pl.effigy.planks === 1 ? '' : 's'} on the effigy.${pl.theme ? ` This week's theme: ${pl.theme.name}.` : ''}`));
    }
  };
  // (P88) the walk-in strips at prime plots' doors, once walking is set up (below)
  let syncPlotDoors: ((d: Commons) => void) | null = null;
  const hearAll = (d: Commons) => {
    setBoard(d.hall.marquee.title, d.hall.marquee.line);
    drawPlots(d); drawPlaya(d); drawMarket(d); drawCart(d); district?.update(d.plots); syncPlotDoors?.(d);
    town.update(d.town, d.market); newLights(town.live);
    // (P36b) whoever turned the windmill in the last few minutes is at its crank, not on the plaza
    const turned = new Set((d.windmill?.turns || []).filter((t) => d.now - t.at < MILL_MINUTES * 60000).map((t) => t.slug));
    const atMill = d.plaza.present.filter((g) => turned.has(g.slug));
    plaza.hear({ ...d.plaza, present: d.plaza.present.filter((g) => !turned.has(g.slug)) }); hearCrews(d);
    for (const g of atMill) { const dn = plaza.dancers.get(g.slug); if (dn) dn.life.gone = true; }
    millCrew.hear({ ...d.plaza, present: atMill, showing: null, chat: [] });
    // a seller who comes to the plaza starts out behind its own stall
    (d.market?.stalls || []).slice(0, STALLS.length).forEach((s, i) => {
      const dn = plaza.dancers.get(s.seller);
      if (!dn || atStall.has(s.seller)) return;
      atStall.add(s.seller);
      (dn.life as any).pos = [STALLS[i][0] - PLAZA.x0 + 0.3, STALLS[i][1] - 1.05 - PLAZA.z0];
    });
    for (const k of [...atStall]) if (!plaza.dancers.has(k)) atStall.delete(k);
    // each place's plazmi, out at the ground for what they're gathering
    for (const [pid, wl] of Object.entries(d.wilds || {})) {
      const by: Record<string, typeof wl.present> = {};
      for (const g of wl.present) { const gr = wildsLand.groundFor(pid, wl.gathering.find((x) => x.slug === g.slug)?.material); if (gr) (by[gr.id] ||= []).push(g); }
      for (const gr of wildsLand.grounds) if (gr.place === pid) gatherers[gr.id]?.hear({ ...wl, present: by[gr.id] || [] });
    }
    assignJobs(d);
    // festival night: everyone out on the playa dances in a ring round the glowing effigy
    const b = d.playa.playa.burned;
    if (b && d.now - b.at < 20 * 60000 && d.playa.present.length && festivalSeen !== b.at) {
      festivalSeen = b.at;
      playaCrowd.startShow({ at: d.now, lead: d.playa.present[0].slug, dancers: d.playa.present.map((g) => g.slug), dance: 'festival', title: 'festival night', line: 'festival night',
        beats: [{ move: 'dance' }, { move: 'hop', times: 2 }, { move: 'twirl' }, { move: 'cheer' }, { move: 'wave' }, { move: 'bow' }] });
    }
    placePeople(atPlaya ? d.playa : d.plaza);
    const nw = Object.values(d.wilds || {}).reduce((a, w) => a + w.present.length, 0);
    const n = d.plaza.present.length + d.playa.present.length + d.hall.inside + nw;
    sub.textContent = mine ? `plot ${focusPlot} on the Commons · ${mine.side}` : atPlaya ? `free-for-all lots round the effigy · ${d.playa.present.length} here`
      : atWilds ? `the woodlot, the quarry, the meadow and the scrapyard · ${nw ? `${nw} gathering` : 'quiet just now'}`
        : `${n ? `${n} plazmi about` : 'Quiet just now'} · ${d.homes} homes on the spiral`;
    hudPeek = atPlaya || atWilds ? sub.textContent || '' : d.plaza.present.length ? `On the plaza: ${d.plaza.present.slice(0, 4).map((g) => g.name).join(', ')}${d.plaza.present.length > 4 ? ` and ${d.plaza.present.length - 4} more` : ''}` : `${d.hall.marquee.title}: ${d.hall.marquee.line}`;
    if (!still) { shell().live(sub.textContent || ''); shell().repeek(); }
    drawHud(d);
  };
  if (data) hearAll(data);

  const dist = +(q.get("dist") || (fp ? 34 : atPlaya ? city.view().dist : atWilds ? wildsLand.view().dist : 106));
  const cam = makeCamera(vw, vh, target, camDir, dist);
  const composer: any = makeComposer(renderer, scene, cam, 'A', vw, vh, PR, { live: !still, tiltBand: 0.24, tiltAmount: 2.6 });
  // live, the page follows the window (fit.ts); stills keep their size
  const fit = still ? null : fitView({ renderer, composer, cam, pr: PR, onFit: (w, h) => { vw = w; vh = h; } });
  // the light of the hour it is in the world: lamp posts, lanterns and the hall's windows brighter after dark
  const daylight = new Daylight(scene, renderer, { sun, hemi }, composer, cam, target, { fixedHour: fixedHourFrom(q) ?? (still ? 17 : null), sunDist: 80 });
  if (atPlaya || atWilds) sun.target.position.set(target.x, 0, target.z); else sun.target.position.set(40, 0, 46);
  sun.target.updateMatrixWorld();
  daylight.glowLamps(scene, [winMats[0]]);
  newLights = (g) => daylight.glowLamps(g, [winMats[0]]);
  if (district) { daylight.glowLamps(district.root, [winMats[0]]); daylight.poolsAt(district.lampSpots); }
  // after dark, pools of light under the plaza's lamps, at the hall's door and along the playa's lanterns
  daylight.poolsAt([...LAMPS.map(([x, z]) => [x, z, 3.2] as [number, number, number]), ...CAMPUS_LAMPS.map(([x, z]) => [x, z, 3.0] as [number, number, number]), [40, HALL.z1 + 2.6, 4.2],
    ...city.pools, ...wildsLand.pools]);
  daylight.apply(true);
  if (data) daylight.setNow(data.now);
  district?.frame(cam, target, scene); wildsLand.frame(cam);
  if (!district) applyView(cam, scene, dist);
  farDetail.frame(cam, true);
  daylight.dome.position.copy(cam.position);
  plaza.update(0); playaCrowd.update(0);
  for (const t of Object.values(gatherers)) t.update(0);
  loadStage(3);
  // the graphics tier (quality.ts): set now, kept up as the frames come
  const gov = govern({ renderer, composer, scene, fit });
  watchFrom(cam);
  composer.render();
  // the loading scene lifts once frames come smoothly (quality.ts)
  if (still) done(); else gov.ready(done);
  if (still) { plaza.pin(cam, vw, vh); playaCrowd.pin(cam, vw, vh); for (const t of Object.values(gatherers)) t.pin(cam, vw, vh); return; }
  homeSign(); guide('commons');
  moveTags(ui); // (P75) the move by each dancer
  {
    const sh = shell();
    if (atPlaya || atWilds) sh.place(placeName, atPlaya ? 'playa' : 'wilds');
    else sh.place(placeName, 'commons', 'See what everyone’s up to ›', '/now', 'commons-now'); // (climb round 6) as a home's and the spiral's
    sh.live(sub.textContent || '');
    onTab('on', atWilds ? 'The wilds' : 'What’s on', 10, () => (hudPeek ? { text: hudPeek } : null)).appendChild(hud);
    sh.primary(atPlaya || atWilds ? { label: '← The plaza', short: 'Plaza', href: '/commons' } : { label: 'The Dance Hall', short: 'Hall', onClick: () => passage('/hall', 'Into the Dance Hall…') });
    waysToGo(atPlaya ? '/commons?at=playa' : atWilds ? '/commons?at=wilds' : '/commons');
    // (P73) who's out and about, live, with Follow: follow from here without leaving
    outAndAboutTab((p) => startFollow(p.slug), 15);
  }
  plaza.prewarm(renderer, cam); playaCrowd.prewarm(renderer, cam);
  for (const t of Object.values(gatherers)) t.prewarm(renderer, cam);

  const controls = new OrbitControls(cam, renderer.domElement);
  controls.target.copy(target);
  controls.enableDamping = true; controls.dampingFactor = 0.08;
  // far enough out to see the whole district (district.ts moves the near and far planes and the fog with it)
  controls.minDistance = 22; controls.maxDistance = district ? 1100 : 240;
  controls.maxPolarAngle = Math.PI * 0.44;
  soundPage({ place: () => (city.inside(controls.target.x, controls.target.z) ? 'playa' : 'commons'), cam, focus: controls.target, daylight, festival: () => burnUntil > performance.now(),
    crowds: () => [plaza, playaCrowd, ...Object.values(gatherers)] as any[], surface: (x, z) => (city.inside(x, z) ? 'sand' : x > PLAZA.x0 && x < PLAZA.x1 && z > PLAZA.z0 && z < PLAZA.z1 ? 'stone' : 'grass'),
    sources: [{ kind: 'fountain', at: { x: FOUNTAIN[0], y: 0.5, z: FOUNTAIN[1] } }, { kind: 'windmill', at: { x: MILL[0], y: 3, z: MILL[1] }, rate: () => millSpin / (2 * Math.PI) }, { kind: 'market', at: { x: 40, y: 1, z: 24 } },
      { kind: 'flag', at: { x: city.arrival().x, y: 3, z: city.arrival().z } }, { kind: 'effigy', at: { x: EFFIGY[0], y: 2, z: EFFIGY[1] }, on: () => burnUntil > performance.now() }] });

  // ----- walking round as your own figure (walk.ts): the plaza, the playa and the wilds, over the bridges
  const crowds = () => [plaza, playaCrowd, ...Object.values(gatherers)];
  const wv = wildsLand.L.outline;
  const NEAR_BOUNDS = { x0: district ? -66 : -1, z0: Math.min(district ? -68 : -1, ...wv.map((p) => p[1] - 1)), x1: Math.max(...wv.map((p) => p[0] + 1)), z1: city.man[1] + city.R + 1 };
  // (P88) walking out of a prime plot further out than the walkable middle (or with its plot framed):
  // walk the lanes round that plot instead, a box about it, so its door and its neighbours are in reach
  const wq0 = walkQuery();
  const away: P | null = wq0?.at ? [wq0.at[0], wq0.at[1]] : fp && dfp ? [fp[0] + 7, fp[1] + 5.5] : null;
  const WALK_BOUNDS = away && (away[0] < NEAR_BOUNDS.x0 || away[1] < NEAR_BOUNDS.z0 || away[0] > NEAR_BOUNDS.x1 || away[1] > NEAR_BOUNDS.z1)
    ? { x0: away[0] - 45, z0: away[1] - 45, x1: away[0] + 45, z1: away[1] + 45 } : NEAR_BOUNDS;
  // through the hall's doors, and in at each of the town's doors: a strip at the door's face, so coming
  // back out (a step further off, ROOMS.out) doesn't send you straight back in; and (P88) in at each
  // prime plot's building door, kept up to date as plots are built (plotDoors)
  const triggers: { rect: { x0: number; z0: number; x1: number; z1: number }; band: number; go: (view: 'first' | 'third') => void; plot?: number }[] = [
    { rect: { x0: 38.4, z0: HALL.z1 + 0.1, x1: 41.6, z1: HALL.z1 + 0.75 }, band: 0, go: (view) => { passage(`/hall?walk=1&view=${view}`, 'Into the Dance Hall…'); } },
    ...doorTriggers()];
  const walk = placeWalk({
    scene, cam, renderer, composer, ui, controls,
    bounds: WALK_BOUNDS, cell: 0.2, band: { base: 0, lo: -1, hi: 1.2 },
    grounds: [
      // the old island, the playa and the wilds (with their bridges, a little below the lawns), and the district
      { x0: 0, z0: 0, x1: 80, z1: 56, y: 0 }, ...townWalkGrounds(), ...city.walkGrounds(), ...wildsLand.walkGrounds(), ...(district ? district.walkGrounds(WALK_BOUNDS) : []),
    ],
    sources: () => [world, playa, wilds, ...(district ? [district.root] : [])],
    start: (from) => from === 'hall' ? { x: 40, z: HALL.z1 + 3.0, yaw: 0 }
      : atPlaya ? city.arrival() : atWilds ? wildsLand.arrival()
        : fp ? { x: fp[0] + 7, z: fp[1] + 10, yaw: Math.PI } : { x: 40, z: PLAZA.z1 - 1.2, yaw: Math.PI },
    rounds: () => [
      ...crowds().flatMap((t) => t.list.filter((d) => d.life.root.visible).map((d) => ({ x: d.life.spot[0] + t.root.position.x, z: d.life.spot[1] + t.root.position.z, r: 0.3, level: 0 }))),
      ...people.children.filter((f) => f.visible).map((f) => ({ x: f.position.x, z: f.position.z, r: BODY_R, level: 0 })),
    ],
    glancers: () => crowds().flatMap((t) => t.list.map((d) => ({ root: d.life.root, at: () => [d.life.spot[0] + t.root.position.x, d.life.spot[1] + t.root.position.z], level: () => 0,
      hello: () => fx.emoji(t.at(d.life).add(new THREE.Vector3(0, 0.6, 0)), '👋', 0.4, 1.8) }))),
    // through the hall's doors: inside, still walking
    triggers,
  });
  syncPlotDoors = (d: Commons) => {
    for (let i = triggers.length - 1; i >= 0; i--) if (triggers[i].plot) triggers.splice(i, 1);
    const into = (n: number) => (view: 'first' | 'third') => { passage(`/plot/${n}?walk=1&from=commons&view=${view}`, 'In through the door…'); };
    // round the plaza: each lot drawn at its corner (PLOTS), facing the plaza
    for (const p of d.plots.plots) {
      const o = PLOTS[p.n], m = p.pavilion?.masses?.[0];
      if (!o || !m || p.status !== 'owned') continue;
      const cx = o[0] + m.x + m.w / 2, fz = o[1] + m.y + m.d;
      triggers.push({ rect: { x0: cx - 0.75, z0: fz - 0.3, x1: cx + 0.75, z1: fz + 0.8 }, band: 0, go: into(p.n), plot: p.n });
    }
    for (const dr of district?.doors() || []) triggers.push({ rect: dr.rect, band: 0, go: into(dr.n), plot: dr.n });
  };
  if (data) syncPlotDoors(data);

  function doorTriggers() {
    return (Object.keys(ROOMS) as RoomId[]).flatMap((id) => {
      const t = TOWN.find((b) => b.id === id); if (!t) return [];
      const [a, b, front = t.d] = DOORS[id], room = ROOMS[id];
      const [x0, z0] = townAt(t, a - 0.15, front - 0.3), [x1, z1] = townAt(t, b + 0.15, front + 0.8);
      return [{ rect: { x0: Math.min(x0, x1), z0: Math.min(z0, z1), x1: Math.max(x0, x1), z1: Math.max(z0, z1) }, band: 0,
        go: (view: 'first' | 'third') => { passage(`${room.path}?walk=1&from=commons&view=${view}`, `Into ${room.name.replace(/^The /, 'the ')}…`); } }];
    });
  }

  // ----- the map camera (camctl.ts, as on the spiral): drag turns, the wheel zooms toward the pointer,
  // right-drag, two fingers or WASD move, Q/E turn, R comes back, and the brass dial. Where it looks
  // stays over the islands: the plaza, the district, the playa and the wilds, measured from what's built
  const reach = new THREE.Box3();
  const measure = () => {
    reach.makeEmpty();
    for (const g of [world, playa, wilds, ...(district ? [district.root] : [])]) reach.union(new THREE.Box3().setFromObject(g));
    reach.min.y = 0; reach.max.y = 3;
  };
  const camctl = camControls({
    controls, cam, dom: renderer.domElement,
    home: () => ({ target, dist, dir: new THREE.Vector3(...camDir) }),
    bounds: () => { if (reach.isEmpty()) measure(); return reach; },
    active: () => !walk.walker() && !walk.busy() && controls.enabled,
    shown: () => !walk.walker(),
  });
  const isClick = clickGuard(renderer.domElement);
  // since you were last here (since.ts): a card at the top of What's on, and what's new sparkles
  if (!atPlaya && !atWilds && !fp) void sinceCommons({ scene, cam, controls, hall, plaza: new THREE.Vector3(40, 0, 32), panel: () => hud.parentElement || hud });

  // (P68) following a plazmi: the camera frames it wherever it is on the Commons (the plaza, the playa, the wilds)
  const troupes = () => [plaza, playaCrowd, ...Object.values(gatherers)];
  const dancerOf = (s: string) => { for (const t of troupes()) { const d = t.dancers.get(s); if (d && !d.leaving) return { t, d }; } return null; };
  const fol = followPage({
    cam, controls, near: 24, far: 40, paused: () => !!walk.walker() || walk.busy(),
    find: (s) => { const x = dancerOf(s); return x ? x.t.at(x.d.life) : null; },
    // (P74) the town, the hall, the mill, trees: the camera keeps the plazmi in sight round them
    solids: () => [world, playa, wilds, ...(district ? [district.root] : [])],
  });
  // a click on a plazmi: its card, with what it's doing and Follow
  const plazmiAt = (cx: number, cy: number) => {
    let best: { slug: string; name: string } | null = null, bd = 34 * 34;
    const v = new THREE.Vector3();
    for (const t of troupes()) for (const d of t.list) {
      v.copy(t.at(d.life)).project(cam);
      const dx = (v.x + 1) / 2 * vw - cx, dy = (1 - v.y) / 2 * vh - cy, dd = dx * dx + dy * dy;
      if (v.z < 1 && dd < bd) { bd = dd; best = { slug: d.g.slug, name: d.g.name }; }
    }
    return best;
  };
  const plazmiText = (s: string, name: string) => {
    for (const [pid, w] of Object.entries(data?.wilds || {})) {
      const g = (w.gathering as { slug: string; material: string; for?: string }[]).find((x) => x.slug === s);
      if (g) return `${name} · gathering ${g.material}${g.for ? ` for the ${g.for}` : ` at ${w.name || pid}`}`;
    }
    return `${name} · ${atPlaya ? 'out on the playa' : atWilds ? 'in the wilds' : 'on the Commons'}`;
  };

  // hover names things; a click on the hall goes in
  const ray = new THREE.Raycaster(), m2 = new THREE.Vector2();
  const tag = el('div', 'tag'); ui.appendChild(tag);
  const hit = (cx: number, cy: number): { text: string; href?: string } | null => {
    m2.set((cx / vw) * 2 - 1, -(cy / vh) * 2 + 1);
    ray.setFromCamera(m2, cam);
    for (const h of ray.intersectObjects([hall, ...plotsG.children, ...pickables, effG, ...stallHits, cartG, ...placeHits, millHit, ...town.hits], true)) {
      for (let o: THREE.Object3D | null = h.object; o; o = o.parent) {
        if (o.userData.town || o.userData.project) { const t = town.hoverText(o, data?.town); if (t) return { text: t, href: roomPath(o.userData.town) }; }
        if (o.userData.build) return { text: buildText(o.userData.build) };
        if (o.userData.mill) { const wm = data?.windmill; return { text: `The Commons windmill · ${wm ? `${wm.weather.replace(/\.$/, '')} · ${wm.turns.length ? `turned by ${wm.turns.map((t) => t.name).join(', ')} today` : 'no turns yet today'}` : 'wind'}` }; }
        if (o.userData.cart) { const c: Cart = o.userData.cart; return { text: `The pedlar's cart · ${(c.things || []).map((t) => `${t.name.toLowerCase()} (${t.coins})`).join(' · ')}` }; }
        if (o.userData.stall) { const st: Stall = o.userData.stall; return { text: `${st.name}'s stall · ${st.items.map((it) => `${it.words} (${it.price})`).join(' · ')}` }; }
        if (o.userData.wild) {
          const wl = data?.wilds?.[o.userData.wild], gr = o.userData.ground;
          const here = (wl?.gathering || []).filter((g) => wildsLand.groundFor(o.userData.wild, g.material)?.id === gr?.id);
          return { text: `${gr ? gr.name.replace(/^the /, 'The ') : wl?.name} · ${gr?.tag || wl?.gives.join(', ')}${here.length ? ` · ${here.map((g) => g.name).join(', ')} gathering` : ''}` };
        }
        if (o === hall) return { text: `The Dance Hall · ${data?.hall.inside ? `${data.hall.inside} inside` : 'go in'}`, href: '/hall' };
        if (o === effG) return { text: `The effigy · ${data?.playa.playa.effigy.planks || 0} planks this week` };
        if (o.userData.plot) {
          const p: Plot = o.userData.plot;
          if (p.status === 'owned') return { text: plotHover(p), href: `/plot/${p.n}` };
          return { text: `Plot ${p.n} · ${p.size[0]} × ${p.size[1]} · ${p.status === 'for-sale' ? `for sale, ${data?.plots.price} coins` : 'Reserved'}` };
        }
        if (o.userData.piece) { const p: Piece = o.userData.piece; return { text: `${p.by_name}'s ${p.kind.replace(/-/g, ' ')}${p.says ? ` “${p.says}”` : ''} (${p.id})${p.admirers ? ` · ${p.admirers} admirer${p.admirers > 1 ? 's' : ''}` : ''}` }; }
      }
    }
    {
      // a lot on the playa, by where the ray meets the ground (the city draws them all as one)
      const g = new THREE.Vector3();
      if (ray.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), g)) {
        const lt = city.lotAt(g.x, g.z);
        if (lt) return { text: city.hoverText(lt, data?.playa.playa.lots.find((l) => l.n === lt.n)) };
      }
    }
    if (district) {
      // the plot under the pointer: its building first, then the ground (P88)
      const p = district.pick(ray);
      if (p && p.ring > 0) return { text: district.hoverText(p), href: district.pageOf(p) || undefined };
    }
    return null;
  };
  let hoverAt: { x: number; y: number } | null = null, down: { x: number; y: number } | null = null, tagFor = 0;
  renderer.domElement.addEventListener('pointermove', (e) => { if (!down && !walk.walker()) hoverAt = { x: e.clientX, y: e.clientY }; });
  renderer.domElement.addEventListener('pointerdown', (e) => { down = { x: e.clientX, y: e.clientY }; if (e.pointerType !== 'mouse') { clearTimeout(tagFor); tag.style.display = 'none'; } });
  renderer.domElement.addEventListener('pointerup', (e) => {
    // only a short, still press is a click; a drag, a pinch or a right-click never is
    if (down && !walk.walker() && isClick(e)) { (window as any).__clicks = ((window as any).__clicks || 0) + 1; const h = hit(e.clientX, e.clientY); if (h?.href) passage(h.href, h.href === '/hall' ? 'Into the Dance Hall…' : h.href.startsWith('/plot/') ? 'Over to the plot…' : 'In through the door…');
      else {
        const p = plazmiAt(e.clientX, e.clientY);
        if (p) plazmiCard({ ...p, text: plazmiText(p.slug, p.name), x: e.clientX, y: e.clientY });
        // (P108) by touch, with no pointer to hover: a tap names the thing, as hovering does
        else if (h && e.pointerType !== 'mouse') {
          tag.textContent = h.text; tag.style.display = 'block';
          tag.style.left = `${Math.min(e.clientX + 14, innerWidth - tag.offsetWidth - 8)}px`; tag.style.top = `${e.clientY + 10}px`;
          clearTimeout(tagFor); tagFor = window.setTimeout(() => { tag.style.display = 'none'; }, 4000);
        }
      } }
    down = null;
  });

  // ----- the loop
  let last = performance.now(), winStart = last, winFrames = 0, winSum = 0, calmFor = 0, beatSeen = 0, notesAt = 0, fliesAt = 0;
  stats.pr = PR;
  const loop = () => {
    const now = performance.now();
    // Battery saver keeps to its frame rate; the governor watches the rest (quality.ts)
    if (!gov.frame(now)) { requestAnimationFrame(loop); return; }
    const dt = Math.min((now - last) / 1000, 0.05);
    winSum += now - last; winFrames++; last = now;
    if (now - winStart > 1000) { stats.fps = Math.round(1000 * winFrames / winSum); PR = gov.pr; stats.pr = PR; winStart = now; winSum = 0; winFrames = 0; }
    daylight.update(dt);
    // the windmill's sails: the day's wind, and faster for a while after someone's taken a turn
    const wm = data?.windmill;
    const lastTurn = wm?.turns.length ? wm.turns[wm.turns.length - 1].at : 0;
    const turned = data && lastTurn ? Math.max(0, 1 - (data.now - lastTurn) / (12 * 60000)) : 0;
    const want = (wm?.wind === 'windy' ? 1.5 : wm?.wind === 'breeze' ? 0.75 : 0.2) + turned * 1.6;
    millSpin += (want - millSpin) * Math.min(1, dt * 0.8);
    millHub.rotation.z -= dt * millSpin;
    if (hoverAt) {
      const h = hit(hoverAt.x, hoverAt.y);
      tag.style.display = h ? 'block' : 'none';
      if (h) { tag.textContent = h.text; tag.style.left = `${hoverAt.x + 14}px`; tag.style.top = `${hoverAt.y + 10}px`; }
      renderer.domElement.style.cursor = h?.href ? 'pointer' : 'grab';
      hoverAt = null;
    }
    plaza.update(dt); playaCrowd.update(dt); fx.update(dt); minePeople();
    for (const t of Object.values(gatherers)) t.update(dt);
    jobs.update();
    // the hall's bulbs chase on the beat, and music drifts out when there's dancing inside
    const beat = Math.floor(Date.now() / BEAT_MS);
    if (beat !== beatSeen) {
      beatSeen = beat;
      const busy = !!data?.hall.showing;
      // the hall's lights are stronger after dark
      const k = 1 + daylight.night * 1.1;
      (winMats[0] as THREE.MeshStandardMaterial).emissiveIntensity = (busy ? (beat % 2 ? 2.4 : 1.4) : 1.6) * k;
      bulbs.forEach((b, i) => ((b.material as THREE.MeshBasicMaterial).color.setScalar((i === beat % 2 ? 2.4 : 0.7) * (0.8 + daylight.night * 0.5))));
    }
    if (data && (data.hall.inside || data.hall.showing) && now > notesAt) { notesAt = now + (data.hall.showing ? 500 : 1400); fx.notes(new THREE.Vector3(40 + (Math.random() - 0.5) * 8, 9.2, 13 + (Math.random() - 0.5) * 6), 1); }
    for (const s of anim.spin) s.rotation.z += dt * 3.2;
    const pulse = 1.8 + Math.sin(now / 400) * 0.8;
    for (const m of anim.glow) (m as THREE.MeshStandardMaterial).emissiveIntensity = pulse;
    // festival night: the effigy glows and rises as fireflies
    swarm.pts.visible = burnUntil > now;
    if (swarm.pts.visible) swarm.update(now / 1000);
    if (burnUntil > now && now > fliesAt) { fliesAt = now + 400; fx.sparkles(new THREE.Vector3(EFFIGY[0] + (Math.random() - 0.5) * 2, 3 + Math.random() * 4, EFFIGY[1] + (Math.random() - 0.5) * 2), '#ffb86b', 4, 0.6); }
    const wk = walk.walker();
    if (wk) wk.update(dt); else if (!walk.busy()) { fol.tick(dt); camctl.tick(dt); controls.update(); camctl.after(); }
    if (hoverAt && wk) { tag.style.display = 'none'; hoverAt = null; }
    district?.frame(cam, controls.target, scene, () => { renderer.shadowMap.needsUpdate = true; }, !!wk);
    if (!district) applyView(cam, scene, wk ? 0 : cam.position.distanceTo(controls.target));
    farDetail.frame(cam);
    wildsLand.frame(cam);
    renderer.info.reset();
    composer.render();
    stats.calls = renderer.info.render.calls; stats.tris = renderer.info.render.triangles;
    plaza.pin(cam, vw, vh); playaCrowd.pin(cam, vw, vh);
    for (const t of Object.values(gatherers)) t.pin(cam, vw, vh);
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);

  const place = atPlaya ? 'playa' : atWilds ? 'woodlot' : 'plaza';
  const talk = new Talk(shell(), {
    kind: 'place', chatOrder: 20, cheer: atPlaya,
    quiet: atPlaya ? 'Quiet out here so far. What goes up, what comes down and what is said shows up here.' : 'Quiet so far. Plazmi and visitors who stop by the plaza show up here.',
    send: async (text) => {
      const j = await fetch(`/api/place/${place}/chat`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ text }) }).then((r) => r.json()).catch(() => ({ ok: false }));
      if (j.ok) poll();
      return { ok: !!j.ok, error: j.error };
    },
    onCheer: async () => {
      const j = await fetch(`/api/place/${place}/cheer`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' }).then((r) => r.json()).catch(() => ({ ok: false }));
      for (const d of playaCrowd.list) fx.emoji(playaCrowd.at(d.life).add(new THREE.Vector3(0, 0.7, 0)), '👏', 0.5);
      return j;
    },
  });
  talk.bind(place, () => data?.now || Date.now());
  const poll = async () => {
    const d = await fetchAll();
    if (!d) return;
    data = d; hearAll(d); talk.refresh(); daylight.setNow(d.now);
  };
  setInterval(poll, POLL_MS);
}

arrival(() => (window as any).__ready === true);
main().catch((e) => { console.error(e); done(); });
